import { useMemo, type FC } from 'react';
import { UiHStack, UiStack, UiText } from '@/lib/ui';
import Item from './Item';
import QueryContainer, { StandardSpinner } from '@/base/QueryContainer/QueryContainer';
import { useGroupRegistrationQuery } from '@/registration/hook/useGroupRegistrationQuery';
import { useGroupCartQuery } from '@/registration/hook/useGroupCartQuery';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useEventSettingsQuery } from '@/registration/hook/useEventSettingsQuery';
import BaseMessageBarError from '@/base/MessageBar/Error';

export interface CartProps {
  showErrors?: boolean
  errors?: string[]
}

const difference = (arr1: string[], arr2: string[]): string[] => { return arr1.filter(x => { return !arr2.includes(x); }); };

const Cart: FC<CartProps> = ({ showErrors, errors = [] }) => {
  const { eventId } = useRegisterRoute();
  const groupRegistrationQuery = useGroupRegistrationQuery(eventId);
  const { data: eventSettings } = useEventSettingsQuery(eventId);
  const cartQuery = useGroupCartQuery(eventId);

  const subchargeLabel = useMemo(
    () => { return eventSettings?.surcharge?.label ?? 'Processing fee'; },
    [eventSettings?.surcharge]
  );

  return (
    <QueryContainer query={groupRegistrationQuery} loadingComponent={<StandardSpinner />}>
      {(groupRegistration) => {
        return (
          <QueryContainer query={cartQuery} errorMessage="Failed to load the shopping cart.">
            {(shoppingCart) => {
              const errorsList = shoppingCart.cartItems.flatMap((itemData) => { return itemData.errors ?? []; });
              return (
                <UiStack flexGrow={1} spacing={4}>
                  {/*<UiText variant={'title'}>Shopping cart</UiText>*/}
                  {shoppingCart.cartItems.map((itemData) => {
                    return (
                      <Item
                        key={itemData.id}
                        itemId={itemData.id}
                        name={itemData.name}
                        quantity={itemData.quantity}
                        priceFormat={itemData.priceFormat}
                        cartId={shoppingCart.id}
                        groupRegistrationId={groupRegistration.id}
                        tempRegistrations={itemData.tempRegistrations}
                        errors={showErrors === true ? itemData.errors : undefined}
                      />
                    );
                  })}
                  {(errors.length > 0) && (
                    <UiStack spacing={4} flexGrow={1} py={4} px={8}>
                      {difference(errors, errorsList).map((error) => {
                        return (
                          <BaseMessageBarError key={error} mt={3}>{error}</BaseMessageBarError>
                        );
                      })}
                    </UiStack>
                  )}
                  <UiHStack justifyContent={'flex-end'} flexGrow={1}>
                    <UiStack py={2} justifyContent={'flex-end'} px={4}>
                      <UiHStack alignItems={'center'} justifyContent={'flex-end'}>
                        <UiText variant={'body1'} color={'text.secondary'}>Subtotal</UiText>
                        <UiText variant={'body1'}>{cartQuery.data?.subtotalFormat ?? 0}</UiText>
                      </UiHStack>
                      {shoppingCart.tax?.map((taxItem, index) => {
                        return (
                          <UiHStack alignItems={'center'} justifyContent={'flex-end'} key={index}>
                            <UiText variant={'body1'} color={'text.secondary'}>{taxItem.label}</UiText>
                            <UiText variant={'body1'}>{taxItem.amountFormat ?? 0}</UiText>
                          </UiHStack>
                        );
                      })}
                      {(!!shoppingCart.surcharge) && (
                        <UiHStack alignItems={'center'} justifyContent={'flex-end'}>
                          <UiText variant={'body1'} color={'text.secondary'}>{subchargeLabel}</UiText>
                          <UiText variant={'body1'}>{shoppingCart.surchargeFormat ?? 0}</UiText>
                        </UiHStack>
                      )}

                      <UiHStack alignItems={'center'} justifyContent={'flex-end'}>
                        <UiText variant={'body1'} color={'text.secondary'}>Total amount</UiText>
                        <UiText variant={'title'}>{cartQuery.data?.totalAmount ?? 0}</UiText>
                      </UiHStack>
                    </UiStack>
                  </UiHStack>
                </UiStack>
              );
            }}
          </QueryContainer>
        );
      }}
    </QueryContainer>
  );
};

export default Cart;
